import { SearchIcon } from "@chakra-ui/icons";
import {
  chakra,
  Grid,
  Stack,
  Text,
  useCheckbox,
  useCheckboxGroup,
  UseRadioProps,
  useId,
} from "@chakra-ui/react";
import { FC, ReactNode, useEffect, useState } from "react";

import { FindRequest } from "@ud/api/newPhpApi/__generated__/newPhpApi.generated";
import { offerType } from "@ud/config/prodeje/config";
import IconArrowDown from "components/Chakra/Icons/ArrowDown";
import IconHouse from "components/Chakra/Icons/House";
import IconPodlazi from "components/Chakra/Icons/Podlazi";
import LocationSuggest from "components/Chakra/LocationSuggest";
import SearchLink from "components/Chakra/SearchLink";
import SelectInput from "components/Chakra/SelectInput";
import { useTracking } from "contexts/Tracking";
import useSearchLocationHistory from "hooks/useSearchLocationHistory";
import { createSearchUrl } from "scenes/Search/createSearchUrl";
import { TFilter } from "scenes/Search/types";
import { ReactFCC } from "src/types/types";

type TItem = { label: string; value: string; id: string };

const arrayTypeOffers: Array<TItem> = Object.values(offerType).map((item) => ({
  value: item.translationId,
  label: item.translationId,
  id: item.id,
}));

interface RadioCardProps extends UseRadioProps {
  text: string;
  icon: ReactNode;
}

const RadioCard: ReactFCC<RadioCardProps> = ({ text, icon, ...props }) => {
  const id = useId(props.id, "propertyType");
  const { getCheckboxProps, getInputProps, htmlProps } = useCheckbox({
    id,
    ...props,
  });

  return (
    <>
      <chakra.label
        display="flex"
        flexDirection="row"
        alignItems="center"
        gridColumnGap={2}
        border="1px solid"
        rounded="lg"
        borderWidth="1px"
        borderColor="ud-grey-300"
        borderRadius="6px"
        transition="all 0.2s"
        _checked={{
          borderColor: "ud-primary",
          bgColor: "ud-primary-100",
        }}
        _disabled={{
          borderColor: "ud-grey-100",
          bgColor: "ud-grey-100",
          cursor: "not-allowed",
        }}
        w="fit-content"
        px="12px"
        py="10px"
        cursor="pointer"
        {...htmlProps}
        {...getCheckboxProps()}
      >
        {icon}
        <Text lineHeight="18px" textAlign="center">
          {text}
        </Text>
        <input {...getInputProps()} hidden />
      </chakra.label>
    </>
  );
};

type PropertyType = FindRequest["propertyType"];

const options: Array<{
  name: string;
  id: Exclude<PropertyType, "land" | "commercial" | "other">;
  icon: ReactNode;
}> = [
  {
    id: "flat",
    name: "Byt",
    icon: <IconPodlazi boxSize="16px" fill="ud-grey-600" />,
  },
  {
    id: "house",
    name: "Dům",
    icon: <IconHouse boxSize="16px" fill="ud-grey-600" />,
  },
];

type SearchValue = {
  typeHousing?: TFilter["propertyType"];
  typeOffer: { id: keyof typeof offerType; label: string } | null;
  location: string;
  isDisabledButton?: boolean;
};

const NewSearchFilter: FC = () => {
  const { trackEvent } = useTracking();
  const [searchValue, setSearchValue] = useState<SearchValue>({
    location: "",
    typeOffer: {
      id: "rent",
      label: "Pronájem",
    },
    isDisabledButton: false,
  });
  const { loadedValues } = useSearchLocationHistory();

  const { getCheckboxProps, setValue } = useCheckboxGroup({
    defaultValue: [],
    onChange: (val) => {
      let finalValue = val[0] as TFilter["propertyType"];
      if (val.length > 1) {
        setValue([val[1]]);
        finalValue = val[1] as keyof TFilter["propertyType"];
      }
      setSearchValue((prev) => ({
        ...prev,
        typeHousing: finalValue,
      }));
    },
  });

  const handler = ({
    loc,
    isDisabled,
  }: {
    loc: string | null;
    isDisabled: boolean;
  }) => {
    setSearchValue((prev) => ({
      ...prev,
      location: loc ?? "",
      isDisabledButton: isDisabled,
    }));
  };

  useEffect(() => {
    if (loadedValues.length) {
      setSearchValue((prev) => ({
        ...prev,
        location: loadedValues[0],
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid
      gridTemplateColumns={{ base: "1fr", md: "auto auto 2fr" }}
      mt={{ base: "24px", md: "80px" }}
      templateAreas={{
        base: `
      "typeOffer typeOffer typeOffer"
      "typeHousing typeHousing typeHousing"
      "location location location"
      "button button button"
      `,
        md: `
      "typeOffer typeHousing location"
      "button button button"
        `,
        lg: `
      "typeOffer typeHousing location button"
        `,
      }}
      backgroundColor="ud-white"
      boxShadow="0px 8px 40px #0000001A"
      borderRadius="6px"
      w="100%"
      gap="24px"
      p="32px"
      alignItems="end"
    >
      <Stack
        direction="column"
        align="flex-start"
        gridArea="typeOffer"
        justify="flex-end"
        w={{ md: "250px" }}
      >
        <Stack direction="row" align="center">
          <Text fontWeight="bold" ml="6px">
            Co hledáte?
          </Text>
        </Stack>
        <SelectInput
          sx={{
            cursor: "pointer",
            color: "ud-grey-600",
            _placeholder: { fontWeight: "normal" },
            _focusVisible: {
              borderWidth: "1px",
              borderColor: "ud-grey-500",
              borderStyle: "solid",
            },
          }}
          extValue={searchValue.typeOffer?.label ?? ""}
          name="typeOffer"
          items={arrayTypeOffers}
          onSelectItem={(value) => {
            setSearchValue((prev) => ({
              ...prev,
              typeOffer: {
                id: value.id as keyof typeof offerType,
                label: value.value,
              },
            }));
          }}
          isReadOnly
          selectedId={searchValue.typeOffer?.id}
          icon={
            <IconArrowDown
              boxSize="18px"
              right="10px"
              top="12px"
              cursor="pointer"
              position="absolute"
            />
          }
        />
      </Stack>
      <Stack
        direction="column"
        align="flex-start"
        justify="flex-end"
        gridArea="typeHousing"
      >
        <Stack direction="row" align="center">
          <Text fontWeight="bold" ml="6px">
            Jakou nemovitost?
          </Text>
        </Stack>
        <Stack direction="row" w="100%">
          {options.map((value) => (
            <RadioCard
              key={value.id}
              icon={value.icon}
              text={value.name}
              isDisabled={searchValue.typeOffer?.id === "coliving"}
              {...getCheckboxProps({ value: value.id })}
            />
          ))}
        </Stack>
      </Stack>
      <Stack direction="column" align="flex-start" flex={1} gridArea="location">
        <Stack direction="row" align="center">
          <Text fontWeight="bold" ml="6px" wordBreak="break-all">
            Kde?
          </Text>
        </Stack>
        <LocationSuggest
          sx={{
            borderWidth: "1px",
            borderColor: "ud-grey-300",
            borderStyle: "solid",
            _focusVisible: {
              borderWidth: "1px",
              borderColor: "ud-grey-500",
              borderStyle: "solid",
            },
          }}
          isLastSearchLocation
          placeholder="Město, ulice..."
          locationValue={searchValue.location}
          onLocationSelect={(loc) => {
            handler({ loc, isDisabled: false });
          }}
          onValueChange={(loc, isDisabledButton) => {
            handler({
              isDisabled: isDisabledButton,
              loc,
            });
          }}
          onClear={() => {
            handler({ loc: "", isDisabled: false });
          }}
        />
      </Stack>
      <SearchLink
        gridArea="button"
        px="24px"
        variant="button"
        h="fit-content"
        cursor={searchValue.isDisabledButton ? "not-allowed" : "pointer"}
        href={createSearchUrl({
          filter: {
            disposition: [],
            offerType: searchValue.typeOffer?.id,
            propertyType: searchValue.typeHousing,
          },
          location: searchValue.location,
        })}
        onClick={(e) => {
          if (searchValue.isDisabledButton) {
            e.preventDefault();
          }
          trackEvent({
            "dl.eCat": "homepage",
            "dl.eAct": "filters.clicked",
            "dl.eLab": "Hledat",
          });
        }}
        aria-label="Hledat bydlení"
        borderRadius="full"
        bg={
          searchValue.isDisabledButton
            ? "linear-gradient(86deg, #822a20 0%, #8e4c1f 100%)"
            : "linear-gradient(86deg, #E74C3C 0%, #E98540 100%)"
        }
        ml="1"
        _focus={{
          bg: searchValue.isDisabledButton
            ? "linear-gradient(86deg, #822a20 0%, #8e4c1f 100%)"
            : "linear-gradient(86deg, #b72211 0%, #f0701b 100%)",
        }}
        _hover={{
          bg: searchValue.isDisabledButton
            ? "linear-gradient(86deg, #822a20 0%, #8e4c1f 100%)"
            : "linear-gradient(86deg, #b72211 0%, #f0701b 100%)",
        }}
      >
        <SearchIcon color="white" mr={1} /> Zobrazit inzeráty
      </SearchLink>
    </Grid>
  );
};

export default NewSearchFilter;
